import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tab, Tabs } from "@mui/material";
import PageCard from "../components/PageCard";
import PetsIcon from "@mui/icons-material/Pets";
import RocketLaunchSharpIcon from "@mui/icons-material/RocketLaunchSharp";
import TrendingUpSharpIcon from "@mui/icons-material/TrendingUpSharp";
import MapView from "../components/MapView";
import { usePage } from "@hooks/usePage";
import { useGetCollarsGeoJson } from "@services/device/collar/getGeoJson";
import { useGetCameraTrapsGeoJson } from "@services/device/cameraTrap/getGeoJson";
import { useGetRegistersGeoJson } from "@services/registers/getGeoJson";
import { useGetReleasesGeoJson } from "@services/releases/getGeoJson";
import { useGetSamplesGeoJson } from "@services/samples/getGeoJson";
import { useGetIndividualsGeoJson } from "@services/individuals/getGeoJson";
import CollarPopup from "@modules/devices/components/collar/CollarPopup";
import CameraTrapPopup from "@modules/devices/components/cameraTrap/CameraTrapPopup";
import RegisterPopup from "@modules/fauna/registers/components/Popup";
import ReleasePopup from "@modules/managingReleases/components/Popup";
import SamplePopup from "@modules/fauna/samples/components/Popup";
import IndividualPopup from "@modules/fauna/individuals/components/Popup";
import type { CollarGeoJsonProperties } from "@services/device/collar/getGeoJson/type";
import type { CameraTrapGeoJsonProperties } from "@services/device/cameraTrap/getGeoJson/type";
import type {
  RegisterGeoJsonProperties,
  RegistersGeoJsonResponse,
} from "@services/registers/getGeoJson/type";
import type {
  ReleaseGeoJsonProperties,
  ReleasesGeoJsonResponse,
} from "@services/releases/getGeoJson/type";
import type {
  SampleGeoJsonProperties,
  SamplesGeoJsonResponse,
} from "@services/samples/getGeoJson/type";
import type {
  IndividualGeoJsonProperties,
  IndividualsGeoJsonResponse,
} from "@services/individuals/getGeoJson/type";
import { mapColorInRegisters } from "@utils/translator/registers";
import { mapColorToReleases } from "@utils/translator/releases";
import { mapColorToSamples } from "@utils/translator/samples";
import { mapColorToIndividuals } from "@utils/translator/individuals";
import { usePermissions } from "@hooks/usePermissions";

type MapType =
  | "collars"
  | "cameraTraps"
  | "registers"
  | "releases"
  | "samples"
  | "individuals";

function Home() {
  const navigate = useNavigate();
  const { openModal } = usePage();
  const [selectedMap, setSelectedMap] = useState<MapType>("collars");
  const { can } = usePermissions();

  const collarsData = useGetCollarsGeoJson(undefined, {
    enabled: selectedMap === "collars",
  });
  const cameraTrapsData = useGetCameraTrapsGeoJson(undefined, {
    enabled: selectedMap === "cameraTraps",
  });
  const registersData = useGetRegistersGeoJson(undefined, {
    enabled: selectedMap === "registers",
  });
  const releasesData = useGetReleasesGeoJson(undefined, {
    enabled: selectedMap === "releases",
  });
  const samplesData = useGetSamplesGeoJson(undefined, {
    enabled: selectedMap === "samples",
  });
  const individualsData = useGetIndividualsGeoJson(undefined, {
    enabled: selectedMap === "individuals",
  });

  const mapConfigs = {
    collars: {
      label: "Rastreadores",
      data: collarsData,
      popupComponent: CollarPopup,
    },
    cameraTraps: {
      label: "Câmeras Trap",
      data: cameraTrapsData,
      popupComponent: CameraTrapPopup,
    },
    registers: {
      label: "Registros",
      data: registersData,
      popupComponent: RegisterPopup,
    },
    releases: {
      label: "Solturas",
      data: releasesData,
      popupComponent: ReleasePopup,
    },
    samples: {
      label: "Amostras",
      data: samplesData,
      popupComponent: SamplePopup,
    },
    individuals: {
      label: "Indivíduos",
      data: individualsData,
      popupComponent: IndividualPopup,
    },
  } as const;

  const currentConfig = mapConfigs[selectedMap];
  const { data, isLoading, error } = currentConfig.data;

  const canCreateRegisters = can("create", "registers");
  const canCreateReleases = can("create", "releases");
  const canReadReports = can("read", "reports");

  const renderMapView = () => {
    switch (selectedMap) {
      case "collars":
        return (
          <MapView<CollarGeoJsonProperties>
            geoJsonData={data}
            popupComponent={CollarPopup}
            isLoading={isLoading}
            error={error}
          />
        );
      case "cameraTraps":
        return (
          <MapView<CameraTrapGeoJsonProperties>
            geoJsonData={data}
            popupComponent={CameraTrapPopup}
            isLoading={isLoading}
            error={error}
          />
        );
      case "registers":
        return (
          <MapView<RegisterGeoJsonProperties>
            geoJsonData={
              data
                ? mapColorInRegisters(data as RegistersGeoJsonResponse)
                : undefined
            }
            popupComponent={RegisterPopup}
            isLoading={isLoading}
            error={error}
          />
        );
      case "releases":
        return (
          <MapView<ReleaseGeoJsonProperties>
            geoJsonData={
              data
                ? mapColorToReleases(data as ReleasesGeoJsonResponse)
                : undefined
            }
            popupComponent={ReleasePopup}
            isLoading={isLoading}
            error={error}
          />
        );
      case "samples":
        return (
          <MapView<SampleGeoJsonProperties>
            geoJsonData={
              data
                ? mapColorToSamples(data as SamplesGeoJsonResponse)
                : undefined
            }
            popupComponent={SamplePopup}
            isLoading={isLoading}
            error={error}
          />
        );
      case "individuals":
        return (
          <MapView<IndividualGeoJsonProperties>
            geoJsonData={
              data
                ? mapColorToIndividuals(data as IndividualsGeoJsonResponse)
                : undefined
            }
            popupComponent={IndividualPopup}
            isLoading={isLoading}
            error={error}
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Tabs
          value={selectedMap}
          onChange={(_, newValue) => setSelectedMap(newValue)}
          textColor="primary"
          indicatorColor="primary"
          aria-label="Seletor de mapas"
          className="ml-4 mt-2"
          variant="scrollable"
          scrollButtons="auto"
        >
          {Object.entries(mapConfigs).map(([key, config]) => (
            <Tab key={key} value={key} label={config.label} />
          ))}
        </Tabs>

        {renderMapView()}
      </div>

      <div className="flex flex-wrap gap-6">
        <PageCard
          title="Novo Registro"
          icon={<PetsIcon className="text-6xl" />}
          buttonText="Novo registro"
          visible={canCreateRegisters}
          onButtonClick={() => {
            navigate("/registers");
            openModal("newRegister");
          }}
        />

        <PageCard
          title="Solturas"
          icon={<RocketLaunchSharpIcon className="text-6xl" />}
          buttonText="Novo Plano"
          visible={canCreateReleases}
          onButtonClick={() => {
            navigate("/managing-releases");
            openModal("newRelease");
          }}
        />

        <PageCard
          title="Relatórios"
          icon={<TrendingUpSharpIcon className="text-6xl" />}
          buttonText="Visualizar relatórios"
          visible={canReadReports}
          onButtonClick={() => {
            navigate("/reports");
          }}
        />
      </div>
    </div>
  );
}

export default Home;
